"use client";

import { Bell } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ZoneLink } from "@/components/layout/zone-link";
import { buttonClass } from "@/components/ui/button";
import { useAnalytics } from "@/lib/analytics/hooks";
import {
	filtersFromSignupAlertParam,
	SIGNUP_ALERT_PARAM,
	signupAlertRoleLabel,
} from "@/lib/experiments/signup-auto-alert";
import {
	confirmSignupAutoAlert,
	getSignupAutoAlertDecision,
} from "@/server/actions/signup-auto-alert";

type Props = { locale: string };

type State =
	| "idle"
	| "offer"
	| "saving"
	| "saved"
	| "limit"
	| "error"
	| "dismissed";

/** Removes the param from the address bar so a refresh never re-offers. */
function clearParam(): void {
	try {
		const url = new URL(window.location.href);
		if (!url.searchParams.has(SIGNUP_ALERT_PARAM)) return;
		url.searchParams.delete(SIGNUP_ALERT_PARAM);
		window.history.replaceState(window.history.state, "", url.toString());
	} catch {
		// Cosmetic only.
	}
}

/**
 * Post-sign-in alert offer (`signup-auto-alert` experiment). Mounted on every
 * public and seeker page; renders nothing unless the URL carries
 * `?signup_alert=` (appended by /signup and /login) AND the server says the
 * account is new. Every new account reports its arm via
 * `signup_auto_alert_eligible`, so the holdout can be read against it.
 */
export function SignupAutoAlert({ locale }: Props) {
	const { posthog } = useAnalytics();
	const ranRef = useRef(false);
	const [param, setParam] = useState<string | null>(null);
	// Safe-off until the server answers: null renders nothing.
	const [enabled, setEnabled] = useState<boolean | null>(null);
	const [state, setState] = useState<State>("idle");

	useEffect(() => {
		if (ranRef.current) return;
		ranRef.current = true;
		let raw: string | null = null;
		try {
			raw = new URL(window.location.href).searchParams.get(SIGNUP_ALERT_PARAM);
		} catch {
			return;
		}
		if (!raw || !filtersFromSignupAlertParam(raw)) {
			clearParam();
			return;
		}
		const value = raw;
		let cancelled = false;
		void getSignupAutoAlertDecision()
			.then((decision) => {
				if (cancelled) return;
				if (!decision.eligible) {
					clearParam();
					return;
				}
				posthog?.capture("signup_auto_alert_eligible", {
					enabled: decision.enabled,
					has_category: value !== "all",
					locale,
				});
				setEnabled(decision.enabled);
				if (!decision.enabled) {
					clearParam();
					return;
				}
				setParam(value);
				setState("offer");
				posthog?.capture("signup_auto_alert_shown", {
					has_category: value !== "all",
					locale,
				});
			})
			.catch(() => {
				// Decision failed: stay hidden (safe-off). Keep the param so a
				// reload retries.
			});
		return () => {
			cancelled = true;
		};
	}, [locale, posthog]);

	if (enabled !== true || param === null || state === "idle") return null;
	if (state === "dismissed") return null;

	const isEn = locale === "en";
	const role = signupAlertRoleLabel(param);

	const confirm = async () => {
		setState("saving");
		try {
			const result = await confirmSignupAutoAlert({
				param,
				locale: isEn ? "en" : "id",
			});
			clearParam();
			if (result.ok) {
				setState("saved");
				posthog?.capture("signup_auto_alert_confirmed", {
					has_category: param !== "all",
					existing: result.existing,
					locale,
				});
				return;
			}
			setState(result.error === "limit_reached" ? "limit" : "error");
			posthog?.capture("signup_auto_alert_failed", {
				error: result.error,
				locale,
			});
		} catch {
			setState("error");
			posthog?.capture("signup_auto_alert_failed", {
				error: "exception",
				locale,
			});
		}
	};

	const dismiss = () => {
		clearParam();
		setState("dismissed");
		posthog?.capture("signup_auto_alert_dismissed", {
			has_category: param !== "all",
			locale,
		});
	};

	if (state === "saved" || state === "limit" || state === "error") {
		let message: string;
		if (state === "saved") {
			message = isEn
				? `Done. New ${role ? `${role} ` : ""}jobs will come to your email. Stop anytime from the link in every email.`
				: `Beres. Lowongan ${role ? `${role} ` : ""}baru akan masuk ke email kamu. Berhenti kapan saja lewat link di setiap email.`;
		} else if (state === "limit") {
			message = isEn
				? "You've reached the alert limit. Remove an older alert to add this one."
				: "Kamu sudah mencapai batas alert. Hapus salah satu alert lama dulu untuk menambah yang baru.";
		} else {
			message = isEn
				? "Couldn't turn on the job alert. Reload this page to try again."
				: "Gagal mengaktifkan alert lowongan. Muat ulang halaman ini untuk mencoba lagi.";
		}
		return (
			<div className="mx-auto max-w-4xl px-4 pt-3">
				<p
					role="status"
					data-testid="signup-auto-alert-result"
					data-state={state}
					className="rounded-xl border border-border/60 bg-card/60 px-4 py-3 text-sm text-foreground"
				>
					{message}{" "}
					{state === "saved" ? (
						<ZoneLink
							href={`/${locale}/saved-searches`}
							className="font-semibold text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{isEn ? "Edit alert" : "Ubah alert"}
						</ZoneLink>
					) : null}
				</p>
			</div>
		);
	}

	const title = isEn
		? role
			? `Get new ${role} jobs by email?`
			: "Get new remote jobs by email?"
		: role
			? `Kirim lowongan ${role} baru ke email kamu?`
			: "Kirim lowongan remote baru ke email kamu?";
	// Only claim "based on what you looked at" when a category was browsed.
	const body = isEn
		? `${role ? "Based on what you just looked at. " : ""}At most one email a day for this alert, stop anytime.`
		: `${role ? "Sesuai yang barusan kamu lihat. " : ""}Paling banyak satu email sehari untuk alert ini, berhenti kapan saja.`;

	return (
		<div className="mx-auto max-w-4xl px-4 pt-3">
			<section
				aria-labelledby="signup-auto-alert-title"
				data-testid="signup-auto-alert"
				className="flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-3 text-foreground sm:flex-row sm:items-center"
			>
				<Bell aria-hidden="true" className="size-5 shrink-0 text-primary" />
				<div className="flex-1">
					<h2
						id="signup-auto-alert-title"
						className="text-sm font-semibold text-foreground"
					>
						{title}
					</h2>
					<p className="text-sm text-muted-foreground">{body}</p>
				</div>
				<div className="flex gap-2">
					<button
						type="button"
						data-testid="signup-auto-alert-confirm"
						onClick={confirm}
						disabled={state === "saving"}
						className={buttonClass({ size: "sm" })}
					>
						{isEn ? "Turn on alert" : "Aktifkan alert"}
					</button>
					<button
						type="button"
						data-testid="signup-auto-alert-dismiss"
						onClick={dismiss}
						disabled={state === "saving"}
						className={buttonClass({ variant: "ghost", size: "sm" })}
					>
						{isEn ? "Not now" : "Nanti saja"}
					</button>
				</div>
			</section>
		</div>
	);
}
